/**
 * 모임 앱 감성 카피 유틸 (i18n 기반)
 * - 시간/요일/계절/사용자 상태에 따라 다채로운 텍스트 생성
 * - 모든 문자열은 locales/{ko,en,ja}.json 의 "tone" 섹션에서 가져옴
 */

import i18n from 'i18next';

const t = (key: string, opts?: any): any => i18n.t(key, opts);
const tArr = (key: string, opts?: any): string[] => {
    const v = i18n.t(key, { ...opts, returnObjects: true }) as any;
    return Array.isArray(v) ? v : [];
};
const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const getTimeBucket = (h: number): 'morning' | 'noon' | 'afternoon' | 'evening' | 'night' | 'midnight' | 'dawn' => {
    if (h < 5) return 'midnight';
    if (h < 10) return 'morning';
    if (h < 12) return 'morning';
    if (h < 14) return 'noon';
    if (h < 17) return 'afternoon';
    if (h < 21) return 'evening';
    if (h < 24) return 'night';
    return 'dawn';
};

const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6;

// ─────────────────────────────────────────────
// 1. 홈 인사말
// ─────────────────────────────────────────────
export const getGreeting = (name: string): { headline: string; sub: string } => {
    const now = new Date();
    const h = now.getHours();
    const bucket = getTimeBucket(h);
    const dow = now.getDay();
    const month = now.getMonth() + 1;
    const date = now.getDate();
    const weekend = isWeekend(now);

    // 특별한 날 우선 (단일 키)
    if (month === 1 && date === 1) {
        return {
            headline: t('tone.greetingHeadline.newYear', { name }),
            sub: t('tone.greetingSub.newYear'),
        };
    }
    if (month === 12 && date >= 24 && date <= 26) {
        return {
            headline: t('tone.greetingHeadline.christmas', { name }),
            sub: t('tone.greetingSub.christmas'),
        };
    }
    if (month === 12 && date === 31) {
        return {
            headline: t('tone.greetingHeadline.yearEnd'),
            sub: t('tone.greetingSub.yearEnd'),
        };
    }

    // 이브닝 분기 결정
    let headlineKey: string;
    let subKey: string;
    if (bucket === 'evening') {
        if (dow === 5) {
            headlineKey = 'tone.greetingHeadline.eveningFriday';
            subKey = weekend ? 'tone.greetingSub.eveningWeekend' : 'tone.greetingSub.eveningWeekday';
        } else if (weekend) {
            headlineKey = 'tone.greetingHeadline.eveningWeekend';
            subKey = 'tone.greetingSub.eveningWeekend';
        } else {
            headlineKey = 'tone.greetingHeadline.eveningWeekday';
            subKey = 'tone.greetingSub.eveningWeekday';
        }
    } else {
        headlineKey = `tone.greetingHeadline.${bucket}`;
        subKey = `tone.greetingSub.${bucket}`;
    }

    const headlines = tArr(headlineKey);
    const subs = tArr(subKey);
    const headline = headlines.length > 0 ? pick(headlines) : t('home.greeting', { name });
    const sub = subs.length > 0 ? pick(subs) : t('home.greetingSub');

    // {{name}} 보간이 array 안에서 처리 안 되는 경우를 대비해 한 번 더 치환
    return {
        headline: typeof headline === 'string' ? headline.replace(/\{\{name\}\}/g, name) : headline,
        sub: typeof sub === 'string' ? sub.replace(/\{\{name\}\}/g, name) : sub,
    };
};

// ─────────────────────────────────────────────
// 2. 빈 모임 상태
// ─────────────────────────────────────────────
export const getEmptyState = (): { title: string; sub: string; cta: string } => {
    const arr = tArr('tone.emptyState');
    if (arr.length === 0) {
        return { title: t('home.emptyToday'), sub: t('home.emptyTodaySub'), cta: t('home.createMeetingBtn') };
    }
    return pick(arr) as any;
};

// ─────────────────────────────────────────────
// 3. 정산 결과 멘트
// ─────────────────────────────────────────────
export const getSettlementNotice = (myBalance: number): string => {
    const amountStr = Math.abs(myBalance).toLocaleString();
    if (myBalance > 0) {
        const pool = tArr('tone.settlementNotice.receive');
        return pool.length ? pick(pool).replace(/\{\{amount\}\}/g, amountStr) : '';
    }
    if (myBalance < 0) {
        const pool = tArr('tone.settlementNotice.pay');
        return pool.length ? pick(pool).replace(/\{\{amount\}\}/g, amountStr) : '';
    }
    const pool = tArr('tone.settlementNotice.even');
    return pool.length ? pick(pool) : '';
};

export const getEmptySettlement = (): string => {
    const arr = tArr('tone.emptySettlement');
    return arr.length ? pick(arr) : '';
};

// ─────────────────────────────────────────────
// 4. 로딩 메시지
// ─────────────────────────────────────────────
export const getLoadingMessage = (kind: 'general' | 'place' | 'friend' | 'cost' | 'flash' = 'general'): string => {
    const arr = tArr(`tone.loading.${kind}`);
    return arr.length ? pick(arr) : '...';
};

// ─────────────────────────────────────────────
// 5. 에러 메시지
// ─────────────────────────────────────────────
export const getFriendlyError = (kind: 'network' | 'unknown' | 'permission' = 'unknown'): string => {
    const arr = tArr(`tone.error.${kind}`);
    return arr.length ? pick(arr) : '';
};

// ─────────────────────────────────────────────
// 6. 토스트
// ─────────────────────────────────────────────
export const getSavedToast = () => {
    const arr = tArr('tone.savedToast');
    return arr.length ? pick(arr) : '';
};

export const getCopiedToast = () => {
    const arr = tArr('tone.copiedToast');
    return arr.length ? pick(arr) : '';
};

// ─────────────────────────────────────────────
// 7. 모임 만들기 / 초대
// ─────────────────────────────────────────────
export const getCreatedMeetingMessage = () => {
    const arr = tArr('tone.createdMeeting');
    return arr.length ? pick(arr) : '';
};

export const getInviteSuccessMessage = () => {
    const arr = tArr('tone.inviteSuccess');
    return arr.length ? pick(arr) : '';
};
